/**
 * Authenticated fetch wrapper with automatic token refresh
 * Handles 401 errors by refreshing the access token and retrying the request
 */

import { buildApiUrl } from '../config/api';

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
};

const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refresh_token');
  
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await fetch(buildApiUrl('/auth/token/refresh/'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      refresh: refreshToken,
    }),
  });

  if (!response.ok) {
    // Refresh token is invalid or expired
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw new Error('Token refresh failed');
  }

  const data = await response.json();
  localStorage.setItem('access_token', data.access);
  
  // If refresh token rotation is enabled, save the new refresh token
  if (data.refresh) {
    localStorage.setItem('refresh_token', data.refresh);
  }
  
  return data.access;
};

/**
 * Authenticated fetch wrapper that automatically refreshes tokens on 401
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @returns {Promise<Response>}
 */
export const authFetch = async (url, options = {}) => {
  // Add auth header if not present
  const token = localStorage.getItem('access_token');
  if (token && !options.headers?.Authorization) {
    options.headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
    };
  }

  // Make the initial request
  let response = await fetch(url, options);

  // If 401 and we have a refresh token, try to refresh
  if (response.status === 401) {
    const refreshToken = localStorage.getItem('refresh_token');
    
    if (!refreshToken) {
      // No refresh token, redirect to login
      window.location.href = '/login';
      throw new Error('Authentication required');
    }

    // If already refreshing, wait for it to complete
    if (isRefreshing) {
      return new Promise((resolve) => {
        subscribeTokenRefresh((newToken) => {
          // Retry original request with new token
          options.headers.Authorization = `Bearer ${newToken}`;
          resolve(fetch(url, options));
        });
      });
    }

    // Start refresh process
    isRefreshing = true;

    try {
      const newToken = await refreshAccessToken();
      isRefreshing = false;
      
      // Notify all waiting requests
      onTokenRefreshed(newToken);

      // Retry original request with new token
      options.headers.Authorization = `Bearer ${newToken}`;
      response = await fetch(url, options);
      
      return response;
    } catch (error) {
      isRefreshing = false;
      refreshSubscribers = [];
      throw error;
    }
  }

  return response;
};

export default authFetch;
